<script>
import { mdiLifebuoy } from '@mdi/js'
import gql from 'graphql-tag'
import { hfTicketsByCustomerId } from '@/api/hf_tickets'
import {
    ticketText as text,
    ticketTimestamp as timestamp,
    formatTicketDate,
    ticketStatusTone,
    normalizeTickets
} from '@/utils/happyfox_tickets'
import DashboardSection from './dashboard_section.vue'
import HappyfoxTicketDetailsDialog from './happyfox_ticket_details_dialog.vue'

/** @typedef {{ id: string, ticketId?: string | number, subject?: string, status?: string,
 * createdat?: string | { value: string } }} SupportTicket */

const BASE_ORGANIZATION_QUERY = gql`
    query ResolveBaseAccountOrganization {
        resolveBaseAccountOrganization {
            orgId
        }
    }
`

export default {
    name: 'HappyfoxSection',
    components: { DashboardSection, HappyfoxTicketDetailsDialog },
    data() {
        return {
            tickets: [],
            organizationId: null,
            loading: false,
            error: '',
            requestId: 0,
            activeContext: null,
            dialog: false,
            selectedTicket: null
        }
    },
    computed: {
        authReady() {
            return !!this.$auth && !this.$auth.loading && this.$auth.isAuthenticated === true && !!this.$auth.user?.id
        },
        isMember() {
            return (
                this.authReady && typeof this.$auth.userInOrganization === 'function' && this.$auth.userInOrganization()
            )
        },
        authContext() {
            const user = this.$auth?.user
            return JSON.stringify([this.authReady, this.isMember, user?.id, user?.org?.branchId, user?.org?.name])
        },
        /** @returns {SupportTicket[]} */
        sortedTickets() {
            return [...this.tickets].sort((a, b) => {
                const first = timestamp(a.createdat)
                const second = timestamp(b.createdat)
                if (first === null) return second === null ? 0 : 1
                return second === null ? -1 : second - first
            })
        },
        section() {
            return {
                id: 'happyfox',
                title: 'Support tickets',
                icon: mdiLifebuoy,
                description: 'Review support tickets for your account organization.',
                link: '/your-account/support-tickets',
                action: 'View tickets',
                rows: this.sortedTickets.slice(0, 3).map((ticket) => ({
                    id: ticket.id,
                    title: `${text(ticket.ticketId)} · ${text(ticket.subject, 'Untitled ticket')}`,
                    detail: `Created ${this.formatDate(ticket.createdat)}`,
                    status: text(ticket.status, 'Unknown'),
                    tone: this.statusTone(ticket.status)
                })),
                footnote: this.loading
                    ? 'Loading organization support tickets…'
                    : this.error
                    ? 'Support tickets are temporarily unavailable'
                    : `Showing ${Math.min(3, this.tickets.length)} of ${
                          this.tickets.length
                      } tickets · Most recent first`
            }
        },
        emptyText() {
            if (!this.authReady) return 'Sign in to view support tickets for your account organization.'
            if (!this.isMember) {
                return 'Your account is not linked to an organization. Ask your administrator for an invitation, or contact Getac Support.'
            }
            return this.organizationId
                ? 'Your organization has no support tickets yet.'
                : 'No organization was found for your account. Contact your organization administrator or Getac Support.'
        }
    },
    watch: {
        authContext: {
            immediate: true,
            handler() {
                this.fetchTickets()
            }
        }
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        formatDate: formatTicketDate,
        statusTone: ticketStatusTone,
        emitSummary() {
            this.$emit('summary', {
                metric: this.loading
                    ? '…'
                    : this.error || !this.organizationId
                    ? '—'
                    : String(this.tickets.length).padStart(2, '0'),
                metricLabel: 'Support tickets'
            })
        },
        async fetchTickets() {
            const context = this.authContext
            if (this.activeContext === context) return
            const requestId = ++this.requestId
            this.activeContext = null
            this.tickets = []
            this.organizationId = null
            this.error = ''
            this.closeDialog()
            this.loading = !!this.$auth?.loading
            if (!this.isMember) {
                this.emitSummary()
                return
            }
            this.activeContext = context
            this.loading = true
            this.emitSummary()
            const isCurrent = () => requestId === this.requestId && context === this.authContext
            try {
                const { data } = await this.$apollo.query({
                    query: BASE_ORGANIZATION_QUERY,
                    fetchPolicy: 'network-only'
                })
                if (!isCurrent()) return
                const base = data?.resolveBaseAccountOrganization
                if (base === null) return
                if (typeof base?.orgId !== 'string' || !base.orgId.trim()) {
                    throw new Error('Base organization unavailable')
                }
                const response = await hfTicketsByCustomerId({ customerId: base.orgId })
                if (!isCurrent()) return
                this.tickets = normalizeTickets(response?.data)
                this.organizationId = base.orgId
            } catch (error) {
                if (isCurrent()) this.error = 'Your support tickets could not be loaded. Please try again.'
            } finally {
                if (isCurrent()) {
                    this.activeContext = null
                    this.loading = false
                    this.emitSummary()
                }
            }
        },
        selectTicket(id) {
            if (!this.isMember || this.loading || this.error) return
            const ticket = this.tickets.find((item) => item.id === id)
            if (!ticket) return
            this.selectedTicket = ticket
            this.dialog = true
        },
        closeDialog() {
            this.dialog = false
            this.selectedTicket = null
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="loading"
        :error="error"
        :empty-text="emptyText"
        loading-text="Loading organization support tickets…"
        selectable
        @retry="fetchTickets"
        @select="selectTicket"
    >
        <template v-slot:details>
            <happyfox-ticket-details-dialog
                :value="dialog"
                :ticket="selectedTicket"
                @input="!$event && closeDialog()"
            />
        </template>
    </dashboard-section>
</template>
